import { AlarmClock, Calendar, Clock } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Duration presets in seconds
 */
const DURATION_PRESETS = [
	{ label: '1 hour', seconds: 3600, icon: Clock },
	{ label: '8 hours', seconds: 28800, icon: Clock },
	{ label: '24 hours', seconds: 86400, icon: Clock },
	{ label: '3 days', seconds: 259200, icon: Calendar },
	{ label: '7 days', seconds: 604800, icon: Calendar },
] as const;

interface SnoozeDurationPickerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelect: (durationSeconds: number) => void;
	title?: string;
	description?: string;
}

export function SnoozeDurationPicker({
	open,
	onOpenChange,
	onSelect,
	title = 'Snooze Link',
	description = 'Choose how long to hide this link. It will reappear in your queue after the selected time.',
}: SnoozeDurationPickerProps) {
	const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
	const [customDate, setCustomDate] = useState<string>('');

	function handlePresetClick(seconds: number) {
		setSelectedDuration(seconds);
		setCustomDate('');
	}

	function handleCustomDateChange(e: React.ChangeEvent<HTMLInputElement>) {
		const dateValue = e.target.value;
		setCustomDate(dateValue);

		if (dateValue) {
			const selectedDate = new Date(dateValue);
			const now = new Date();
			const diffMs = selectedDate.getTime() - now.getTime();

			if (diffMs > 0) {
				setSelectedDuration(Math.floor(diffMs / 1000));
			} else {
				setSelectedDuration(null);
			}
		} else {
			setSelectedDuration(null);
		}
	}

	function handleSubmit() {
		if (selectedDuration && selectedDuration > 0) {
			onSelect(selectedDuration);
			onOpenChange(false);
			// Reset state
			setSelectedDuration(null);
			setCustomDate('');
		}
	}

	function handleClose() {
		onOpenChange(false);
		setSelectedDuration(null);
		setCustomDate('');
	}

	// Calculate minimum date for custom picker (now)
	const minDate = new Date().toISOString().slice(0, 16);

	// Format selected duration for display
	function formatDuration(seconds: number): string {
		const hours = Math.floor(seconds / 3600);
		const days = Math.floor(hours / 24);

		if (days > 0) {
			const remainingHours = hours % 24;
			if (remainingHours === 0) {
				return `${days} day${days !== 1 ? 's' : ''}`;
			}
			return `${days} day${days !== 1 ? 's' : ''}, ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
		}
		return `${hours} hour${hours !== 1 ? 's' : ''}`;
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlarmClock className="size-5" />
						{title}
					</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* Preset durations */}
					<div className="grid grid-cols-2 gap-2">
						{DURATION_PRESETS.map((preset) => {
							const Icon = preset.icon;
							const isSelected = selectedDuration === preset.seconds && !customDate;
							return (
								<button
									key={preset.seconds}
									type="button"
									onClick={() => handlePresetClick(preset.seconds)}
									className={cn(
										'flex items-center gap-2 px-4 py-3 rounded-lg border text-left',
										'transition-colors hover:bg-accent hover:border-primary/50',
										isSelected && 'bg-primary/10 border-primary'
									)}
								>
									<Icon className={cn('size-4', isSelected ? 'text-primary' : 'text-muted-foreground')} />
									<span className={cn('font-medium', isSelected && 'text-primary')}>
										{preset.label}
									</span>
								</button>
							);
						})}
					</div>

					{/* Custom date/time */}
					<div className="space-y-2">
						<label htmlFor="custom-date" className="text-sm font-medium text-muted-foreground">
							Or pick a custom date:
						</label>
						<input
							id="custom-date"
							type="datetime-local"
							value={customDate}
							min={minDate}
							onChange={handleCustomDateChange}
							className={cn(
								'w-full px-3 py-2 rounded-lg border bg-background',
								'focus:outline-none focus:ring-2 focus:ring-primary/50',
								customDate && 'border-primary'
							)}
						/>
					</div>

					{/* Selected duration preview */}
					{selectedDuration && selectedDuration > 0 && (
						<div className="text-sm text-center text-muted-foreground">
							Will reappear in <span className="font-medium text-foreground">{formatDuration(selectedDuration)}</span>
						</div>
					)}
				</div>

				<DialogFooter className="flex-row justify-end gap-2">
					<Button variant="outline" onClick={handleClose}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={!selectedDuration || selectedDuration <= 0}>
						<AlarmClock className="size-4 mr-1" />
						Snooze
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
